import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { breadcrumbs, faqPage, aeoGeo } from "@/lib/seo/schema";

const ROWS: { dimension: string; pulse: string; prime: string }[] = [
  {
    dimension: "Modell",
    pulse: "Abonnemang — löpande leverans varje månad",
    prime: "Projekt — en skräddarsydd produktion åt gången",
  },
  {
    dimension: "Tempo",
    pulse: "Veckotakt. Brief → leverans på dagar",
    prime: "Veckor till månader, beroende på omfång",
  },
  {
    dimension: "Format",
    pulse: "Social-film, brand-snuttar, versionering, klipp av befintligt material",
    prime: "Reklamfilm, dokumentär, employer branding, kampanjfilm",
  },
  {
    dimension: "Produktionsvärde",
    pulse: "Effektivt, repeterbart, rätt-nog för flödet",
    prime: "Högt — regi, foto, ljus, ljud, grade i toppskikt",
  },
  {
    dimension: "Pris",
    pulse: "Fast månadskostnad, förutsägbar budget",
    prime: "Offert per produktion",
  },
  {
    dimension: "Passar bäst för",
    pulse: "Team som behöver kontinuerlig film utan projektfriktion",
    prime: "Lanseringar, kampanjer, employer branding, brand-film",
  },
];

const FAQ = [
  {
    q: "Vad är skillnaden mellan Pulse och Prime?",
    a: "Pulse är ett filmabonnemang med löpande leverans varje månad till fast pris — för team som behöver kontinuerlig social- och brand-film. Prime är skräddarsydd produktion per projekt — för enskilda större filmer med högre produktionsvärde, som reklamfilm, dokumentär och employer branding.",
  },
  {
    q: "Kan man kombinera Pulse och Prime?",
    a: "Ja. De flesta större kunder kör Pulse för det löpande flödet och bokar Prime för enstaka kampanjer, lanseringar eller employer branding-filmer.",
  },
  {
    q: "Vad kostar Pulse jämfört med Prime?",
    a: "Pulse är en fast månadskostnad — du vet vad du betalar och vad du får. Prime offereras per produktion eftersom omfång, regi, location och post-produktion varierar.",
  },
  {
    q: "Vilken modell passar oss?",
    a: "Om ni behöver film varje månad (sociala kanaler, employer branding, säljmaterial) ger Pulse lägst total kostnad och högst tempo. Om ni har en specifik kampanj, lansering eller varumärkesfilm med högt produktionsvärde — välj Prime.",
  },
  {
    q: "Levererar Fenny i hela Sverige?",
    a: "Ja. Vi producerar för kunder i hela Sverige och tar oss till location där produktionen kräver det.",
  },
];

export const Route = createFileRoute("/jamfor/pulse-vs-prime")({
  head: () => ({
    meta: [
      { title: "Pulse vs Prime — jämför pris och upplägg | Fenny" },
      {
        name: "description",
        content:
          "Filmabonnemang eller skräddarsydd produktion? Side-by-side jämförelse mellan Fenny Pulse och Fenny Prime — tempo, format, pris och när du ska välja vad.",
      },
      {
        property: "og:title",
        content: "Pulse vs Prime — vilken modell passar er?",
      },
      {
        property: "og:description",
        content:
          "Filmabonnemang vs skräddarsydd produktion. Jämför tempo, format och pris — välj rätt modell för er.",
      },
      { property: "og:url", content: "https://fenny.se/jamfor/pulse-vs-prime" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/jamfor/pulse-vs-prime" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/jamfor/pulse-vs-prime", name: "Pulse vs Prime — vilken modell passar er? | Fenny", description: "Filmabonnemang eller skräddarsydd produktion? Side-by-side jämförelse mellan Fenny Pulse och Fenny Prime — tempo, format, pris och när du ska välja vad."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqPage(FAQ)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Jämför", path: "/jamfor/pulse-vs-prime" },
            { name: "Pulse vs Prime", path: "/jamfor/pulse-vs-prime" },
          ]),
        ),
      },
    ],
  }),
  component: ComparePage,
});

function ComparePage() {
  return (
    <Section className="pt-32 pb-20">
      <div className="container-wide max-w-4xl">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Jämför
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight">
          Pulse vs Prime — vilken modell passar er?
        </h1>

        {/* TL;DR — AEO citation block */}
        <p className="mt-6 text-lg text-muted-foreground">
          <strong className="text-foreground">Kort svar:</strong> Välj{" "}
          <Link to="/pulse" className="underline underline-offset-4">
            Pulse
          </Link>{" "}
          om ni behöver film varje månad till fast pris. Välj{" "}
          <Link to="/prime" className="underline underline-offset-4">
            Prime
          </Link>{" "}
          om ni har en specifik produktion (reklam, dokumentär, employer
          branding) som kräver högre produktionsvärde.
        </p>

        <QuickAnswer answerId="sv_pulse_vs_prime_val" className="mt-8" />



        <div className="mt-12 overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Dimension</th>
                <th className="px-4 py-3 text-left font-medium">Pulse</th>
                <th className="px-4 py-3 text-left font-medium">Prime</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.dimension} className="border-t border-white/10">
                  <td className="px-4 py-3 font-medium text-foreground">
                    {r.dimension}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{r.pulse}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.prime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-semibold tracking-tight">
            Vanliga frågor
          </h2>
          <dl className="mt-6 space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-semibold text-foreground">{f.q}</dt>
                <dd className="mt-1 text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-16 flex flex-wrap gap-3">
          <Button to="/pulse" variant="mint" arrow>
            Läs om Pulse
          </Button>
          <Button to="/prime" variant="outline" arrow>
            Läs om Prime
          </Button>
          <Button to="/kontakt" variant="ghost" arrow>
            Boka samtal
          </Button>
        </div>
      </div>
    </Section>
  );
}
